import { Box, Button, Card, Typography, LinearProgress } from '@mui/material';
import React, { useState, useRef } from 'react';
import { CloudUpload, VideoFile } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { MediaServices } from './MediaServices';

const VideoUpload = ({ onUploadSuccess, currentTab }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('video/')) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid File',
                    text: 'Please select a valid video file (MP4, AVI, MOV, etc.)',
                });
                return;
            }
            
            // Validate file size (max 100MB)
            const maxSize = 100 * 1024 * 1024; // 100MB in bytes
            if (file.size > maxSize) {
                Swal.fire({
                    icon: 'error',
                    title: 'File Too Large',
                    text: 'Video file must be less than 100MB',
                });
                return;
            }
            
            setSelectedFile(file);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            Swal.fire({
                icon: 'warning',
                title: 'No File Selected',
                text: 'Please select a video file first',
            });
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            // Simulate progress (since commonRequest doesn't support progress tracking)
            const progressInterval = setInterval(() => {
                setUploadProgress((prev) => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return 90;
                    }
                    return prev + 10;
                });
            }, 500);

            const path = currentTab === 'whatsapp' ? 'whatsapp' : 'all';
            const response = await MediaServices.uploadVideo(selectedFile, path);
            
            clearInterval(progressInterval);
            setUploadProgress(100);

            if (response.success === '1' || response.success === 1) {
                Swal.fire({
                    icon: 'success',
                    title: 'Upload Successful',
                    text: 'Video has been uploaded successfully',
                    timer: 1500,
                });
                
                // Reset form
                setSelectedFile(null);
                setUploadProgress(0);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                
                // Refresh list
                if (onUploadSuccess) {
                    onUploadSuccess();
                }
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Upload Failed',
                    text: response.message || 'Failed to upload video',
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Upload Error',
                text: 'Something went wrong while uploading the video',
            });
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) {
            if (!file.type.startsWith('video/')) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid File',
                    text: 'Please drop a valid video file',
                });
                return;
            }
            
            const maxSize = 100 * 1024 * 1024;
            if (file.size > maxSize) {
                Swal.fire({
                    icon: 'error',
                    title: 'File Too Large',
                    text: 'Video file must be less than 100MB',
                });
                return;
            }
            
            setSelectedFile(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    return (
        <Card sx={{ height: '100%' }}>
            <Box sx={{ p: '.5% 1%', border: '2px solid lightGray', bgcolor: 'lightGray' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Upload Video</Typography>
            </Box>
            
            <Box sx={{ p: 3 }}>
                {/* Drop Zone */}
                <Box
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                        border: '2px dashed #ccc',
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        cursor: 'pointer',
                        bgcolor: selectedFile ? '#e3f2fd' : '#fafafa',
                        transition: 'all 0.3s',
                        '&:hover': {
                            borderColor: '#1976d2',
                            bgcolor: '#f5f5f5',
                        },
                    }}
                >
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept="video/*"
                        style={{ display: 'none' }}
                    />
                    
                    {selectedFile ? (
                        <Box>
                            <VideoFile sx={{ fontSize: 48, color: '#1976d2', mb: 1 }} />
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                                {selectedFile.name}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                            </Typography>
                        </Box>
                    ) : (
                        <Box>
                            <CloudUpload sx={{ fontSize: 48, color: '#aaa', mb: 1 }} />
                            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                                Drag & drop a video file here
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1 }}>
                                or click to browse
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 1 }}>
                                Supported: MP4, AVI, MOV (Max: 100MB)
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Progress Bar */}
                {isUploading && (
                    <Box sx={{ mt: 2 }}>
                        <LinearProgress 
                            variant="determinate" 
                            value={uploadProgress} 
                            sx={{ height: 8, borderRadius: 4 }}
                        />
                        <Typography sx={{ fontSize: '0.75rem', textAlign: 'center', mt: 1 }}>
                            Uploading... {uploadProgress}%
                        </Typography>
                    </Box>
                )}

                {/* Upload Button */}
                <Button
                    variant="contained"
                    fullWidth
                    sx={{ mt: 3 }}
                    onClick={handleUpload}
                    disabled={!selectedFile || isUploading}
                    startIcon={<CloudUpload />}
                >
                    {isUploading ? 'Uploading...' : 'Upload Video'}
                </Button>
            </Box>
        </Card>
    );
};

export default VideoUpload;
